/**
 * <RecordShell> — what a RECORD looks like, as a control (#964).
 *
 * ── why this had to move ────────────────────────────────────────────────────
 * #964's binding comment names four shared things a customer page must use, and
 * the third is *"a record — the console's record body shape: master left,
 * related right"*, with *"a bespoke portal detail page"* listed as not
 * acceptable. That shape lived in `apps/console/src/record/RecordBody.tsx` and
 * its stylesheet, and an app cannot import from another app — the identical
 * position `<DataGrid>` was in before #959.
 *
 * What could NOT move is `RecordBody` itself, and it is worth being precise
 * about why rather than leaving it to look like a half-measure. `RecordBody` is
 * a metamodel renderer: it resolves `core.entity_type` + `field_meta` +
 * `core.form` for a TABLE and writes back through `useSaveEntity`. The portal's
 * records are not tables — `portal.my_projects`, `portal.my_case` and
 * `portal.my_invoices` are governed FUNCTIONS with hand-chosen return columns,
 * which is the entire point of a bridge (a customer reads a narrowed projection,
 * not a row). There is no entity handle to hand `RecordBody`, and inventing one
 * would mean giving an external principal a table read that the bridges
 * deliberately withheld.
 *
 * So the split is the same one #959 drew for the grid: what a record IS (the
 * identity bar, and master-left/related-right at every depth) lives here, and
 * what an application DOES with one — resolve a form, save it, cascade its
 * relations — stays in the app. The console's `RecordBody` renders THROUGH this
 * component, so the two cannot drift: it is not a portal imitation of the
 * console's record shape, it is the shape, used twice.
 *
 * ── the mark, and the palette that may not exist ────────────────────────────
 * Christopher's canvas model: "each form always has a palette button on top,
 * left of the title of the form." `onCommand` is therefore what makes the mark a
 * BUTTON — and the portal has no palette (`paletteScope` and ⌘K are console
 * files). Same rule as `GridBar` and as `<DataGrid>`'s selection column: an
 * affordance a host should not offer is a prop it does not pass, and the mark
 * renders as chrome instead. Not a no-op handler — a focusable control announced
 * as a command affordance that does nothing is worse than none.
 *
 * ── no data dependency, deliberately ────────────────────────────────────────
 * Nothing here imports `@solum/sdk`, knows an entity key, or fetches anything.
 * That absence is what keeps `@solum/ui` a control kit, and it is the same
 * invariant `grid/DataGrid.tsx` states about itself.
 */
import type { ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { MarbleMark } from '../MarbleMark'
import s from './RecordShell.module.css'

export interface RecordShellProps {
  /** The record's display name. Rendered as the heading. */
  heading: ReactNode
  /**
   * `page` is a full route; `pane` is the aside beside a grid.
   *
   * Drives the type scale (the stylesheet keys on `data-presentation`) and the
   * heading LEVEL — a pane sits under the page's own `<h1>`, so its record title
   * is an `<h2>`. Getting that wrong is a real heading-order violation, not a
   * cosmetic one.
   */
  presentation?: 'page' | 'pane'
  /** Overrides the level `presentation` would choose. */
  headingLevel?: 1 | 2 | 3
  headingTestId?: string

  /**
   * Opens the palette scoped to THIS record.
   *
   * Omit only where the host has no palette — the mark then renders as chrome.
   */
  onCommand?: () => void
  /** Accessible name for the mark button: "Commands for Northwind". */
  commandLabel?: string
  commandTestId?: string
  /** Contextual record chrome, normally the form's compact overflow menu. */
  actions?: ReactNode

  /**
   * What this record IS, beside its name — a strip of governed identity marks.
   *
   * A SLOT, not a list of strings, for the reason `related` and `process` are:
   * this component knows no entity key and must not learn what a "role" is. The
   * host renders the marks its own registry supplies, or passes nothing and the
   * strip is absent from the DOM rather than an empty box beside the title.
   *
   * It sits AFTER the heading and before `actions`, so the accessible name of
   * the heading stays the record's name. Putting these inside `heading` would
   * fold "customer" and "partner" into the h1's own text, which is what the
   * console's legacy party page did.
   */
  badges?: ReactNode
  badgesTestId?: string

  /**
   * The record's flow position — a `<ProcessBar>` (§4.3 of the work-surface
   * RFC / #1000 addendum 6, which places the bar in the record body, above the
   * master/related split). A slot rather than a baked-in bar for the same
   * reason `related` is one: this component knows nothing about flows, and a
   * record with no flow instance must not carry an empty rail.
   */
  process?: ReactNode

  /**
   * The record's measured CONTEXT — a strip of governed figures under the flow
   * position (#2267 port 6). "Insight" is the console's existing word for
   * exactly this kind of statement about a record: see `ShellDrawer`'s
   * `RECORD_INSIGHT`.
   *
   * A slot for the same reason `process` is one: this component does not know
   * what a measure is, cannot fetch one, and a record whose deployment
   * registers none must not carry an empty band. Rendered AFTER `process`,
   * which is where the legacy CRM header put it — position first, then the
   * numbers that describe the position.
   */
  insight?: ReactNode
  /**
   * The record's governed FACTS under its name — "Q8 · q8.com · BE · 9
   * connections" in the leading design (#2406, temp-design Organization Detail).
   * A slot for the reason `badges` is one: this component knows no field and no
   * entity, so the host composes the line from what its registry marks as a
   * summary, and a record with nothing to say passes nothing — the line is then
   * absent, not an empty row under the title.
   */
  facts?: ReactNode
  factsTestId?: string
  /**
   * The insight's DISCLOSURE ROW — the design's "⌄ METRICS … Click to collapse",
   * a thin full-width row in the cap between the flow position and the figures.
   *
   * It was a ghost button in the identity row (`insightToggle`), which fixed the
   * loud bordered button the owner rejected but put a view control between the
   * record's name and its acts. The leading design draws it as the head of the
   * figures it opens, so this component owns its anatomy and the host only
   * states the facts of it: whether it is open, what it controls, how to toggle.
   * No descriptor ⇒ no row.
   */
  insightDisclosure?: {
    expanded: boolean
    onToggle: () => void
    /** The id of the element in `insight` that the row shows and hides. */
    controls: string
    /** The engraved word on the row. */
    label: string
    /** Accessible names for the two states; each must contain `label`. */
    expandLabel: string
    collapseLabel: string
    testId?: string
  }

  /**
   * The record's RELATED items — rendered in the right column.
   *
   * Present ⇒ two columns. Absent ⇒ the master occupies the full width, which is
   * what the console's record PAGE does (it renders its relations below the
   * card) and what a record with nothing related must do anyway.
   */
  related?: ReactNode
  relatedTestId?: string

  testId?: string
  /** The master — the form, the facts, the read-only body. */
  children: ReactNode
}

export function RecordShell({
  heading, presentation = 'page', headingLevel, headingTestId,
  onCommand, commandLabel, commandTestId, actions, badges, badgesTestId,
  facts, factsTestId, process, insight, insightDisclosure, related, relatedTestId, testId, children,
}: RecordShellProps) {
  const level = headingLevel ?? (presentation === 'page' ? 1 : 2)
  const H = (`h${level}` as 'h1' | 'h2' | 'h3')
  return (
    <div data-presentation={presentation} data-testid={testId} className={s.body}>
      <div className={s.feature} data-testid="record-feature-header">
        <header className={s.idbar}>
          {onCommand ? (
            <button type="button" className={s.cmd} data-testid={commandTestId}
              aria-label={commandLabel} title={commandLabel}
              aria-haspopup="dialog"
              onClick={onCommand}>
              <MarbleMark size={20} />
            </button>
          ) : (
            <span className={s.cmd} data-mark aria-hidden="true" data-testid={commandTestId}>
              <MarbleMark size={20} />
            </span>
          )}
          {/* The name and what the record IS on one line, its facts under it —
              one column, so a long name wraps within the identity rather than
              pushing the acts off the row. */}
          <div className={s.ident}>
            <div className={s.titleRow}>
              <H className={s.title} data-testid={headingTestId}>{heading}</H>
              {badges != null && <div className={s.badges} data-testid={badgesTestId}>{badges}</div>}
            </div>
            {facts != null && <div className={s.facts} data-testid={factsTestId}>{facts}</div>}
          </div>
          {actions != null && <div className={s.actions}>{actions}</div>}
        </header>
        {process != null && <div className={s.process}>{process}</div>}
        {insightDisclosure != null && (
          <button type="button" className={s.disclosureRow}
            data-testid={insightDisclosure.testId}
            aria-expanded={insightDisclosure.expanded}
            aria-controls={insightDisclosure.controls}
            aria-label={insightDisclosure.expanded ? insightDisclosure.collapseLabel : insightDisclosure.expandLabel}
            onClick={insightDisclosure.onToggle}>
            <ChevronRight className={s.disclosureChevron} size={11} strokeWidth={2.8} aria-hidden="true" />
            <span className={s.disclosureLabel}>{insightDisclosure.label}</span>
            {/* Says what a click will DO, in the design's words, and is
                decoration for assistive technology: the state is on
                `aria-expanded` and the act is in the accessible name. */}
            <span className={s.disclosureHint} aria-hidden="true">
              {insightDisclosure.expanded ? 'Click to collapse' : 'Click to expand'}
            </span>
          </button>
        )}
        {insight != null && <div className={s.insight}>{insight}</div>}
      </div>
      <div className={related != null ? s.cols : undefined}>
        <div className={s.master}>{children}</div>
        {related != null && (
          <div className={s.related} data-testid={relatedTestId}>{related}</div>
        )}
      </div>
    </div>
  )
}
